import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Select, MenuItem, Tabs, Tab, TextField, Card, CardContent, Grow,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import PlanVsActualStepper from "./PlanVsActualStepper";
import PlanVsActualManStepper from "./PlanVsActualManStepper";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function GeneratePlanvsActual() {
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Default to today
  const [dateRange, setDateRange] = useState({ dates: "", time: "", monthYear: "" });
  const [machineGroups, setMachineGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [machines, setMachines] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setChecked(true); // Trigger animation on component mount
  }, []);

  useEffect(() => {
    // Automatically update the date range when the date changes
    const fromDay = selectedDate.date();
    const toDay = selectedDate.add(1, "day").date();
    const dates = `${fromDay} - ${toDay}`;
    const time = `08:00 AM`;
    const monthYear = selectedDate.format("MMMM YYYY");
    setDateRange({ dates, time, monthYear });
  }, [selectedDate]);

  useEffect(() => {
    // Fetch machine groups on component mount
    axios
      .get(`${API_BASE_URL}/MachineGroupCodeWithNames`)
      .then((response) => {
        setMachineGroups(response.data);
      })
      .catch((error) => {
        console.error("Error fetching machine groups:", error);
      });
  }, []);

  const handleGroupChange = (event) => {
    const groupCode = event.target.value;
    setSelectedGroup(groupCode);
    setActiveTab(0); // Reset tab on group change

    // Fetch machines for the selected group (with HoursType)
    axios
      .get(`${API_BASE_URL}/MachineCodeWithNames?groupCode=${groupCode}`)
      .then((response) => {
        console.log("Machines fetched:", response.data);
        // Log each machine's HoursType for debugging
        response.data.forEach(machine => {
          console.log(`${machine.MachineCode}: HoursType = ${machine.HoursType}`);
        });
        setMachines(response.data);
      })
      .catch((error) => {
        console.error("Error fetching machines:", error);
      });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ✅ KEY ADDITION: Get current machine's HoursType
  const currentMachine = machines[activeTab];
  const hoursType = currentMachine?.HoursType || 'Machine'; // Default to 'Machine' if not set

  console.log("Current Machine:", currentMachine);
  console.log("Hours Type:", hoursType);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Card
          sx={{
            background: "linear-gradient(to bottom,rgb(249, 249, 249),rgb(189, 223, 252))",
            borderRadius: "8px",
            boxShadow: 2,
            marginBottom: "10px",
          }}
        >
          <CardContent>
            <Grid container spacing={1.5}>
              {/* Date Picker */}
              <Grow in={checked} timeout={500}>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                    Select Date
                  </Typography>
                  <DatePicker
                    label="Pick Date"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grow>

              {/* Machine Group Selector */}
              <Grow in={checked} timeout={1000}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                    Select Group
                  </Typography>
                  <Select
                    fullWidth
                    value={selectedGroup}
                    onChange={handleGroupChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Work-stations Group
                    </MenuItem>
                    {machineGroups.map((group) => (
                      <MenuItem key={group.MachineGroupCode} value={group.MachineGroupCode}>
                        {group.GroupDescription}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grow>

              {/* Date-Time Range Text */}
              <Grow in={checked} timeout={1500}>
                <Grid item xs={12} sm={6} md={1.5}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                    Time Range
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#0d47a1" }}>
                    {dateRange.dates}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontSize: "0.9rem", fontWeight: 400, color: "#757575" }}
                    >
                      {" "}
                      {dateRange.time}
                    </Typography>
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: "gray" }}>
                    {dateRange.monthYear}
                  </Typography>
                </Grid>
              </Grow>

              {/* Tabs for Machines */}
              {selectedGroup && machines.length > 0 && (
                <Grow in={checked} timeout={2000}>
                  <Grid item xs={12} sm={6} md={5.5}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      Work-stations
                      {/* ✅ Show badge indicating Machine/Man hours */}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 1,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: hoursType === 'Man' ? '#ff9800' : '#2196f3',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        {hoursType === 'Man' ? 'Man-Hour' : 'Machine-Hour'}
                      </Typography>
                    </Typography>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {machines.map((machine) => (
                        <Tab 
                          key={machine.MachineCode} 
                          label={machine.MachineCode}
                          sx={{
                            borderBottom: machine.HoursType === 'Man' ? '3px solid #ff9800' : 'none'
                          }}
                        />
                      ))}
                    </Tabs>
                  </Grid>
                </Grow>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* ✅ KEY CHANGE: Route to correct stepper based on HoursType */}
        {selectedGroup && machines.length > 0 ? (
          <>
            {hoursType === 'Man' ? (
              // Man-Hour Stepper
              <PlanVsActualManStepper
                key={machines[activeTab].MachineCode}
                selectedDate={selectedDate}
                machineCode={machines[activeTab].MachineCode}
              />
            ) : (
              // Machine-Hour Stepper
              <PlanVsActualStepper
                key={machines[activeTab].MachineCode}
                selectedDate={selectedDate}
                machineCode={machines[activeTab].MachineCode}
              />
            )}
          </>
        ) : (
          selectedGroup && (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 3 }}>
              No machines available for the selected group.
            </Typography>
          )
        )}
      </Box>
    </LocalizationProvider>
  );
}